import { Link } from "~/components/Link";
import { ImageLoader } from "~/components/ImageLoader";
import { TableCard } from "~/components/TableCard";
import { RWA_COIN_ICON } from "~/consts";
import { DateDisplay } from "~/components/DateDisplay";
import { Badge, BadgeVariants } from "~/components/Badge";
import { ColumnSpanConfig } from "~/types";
import { roundN } from "~/utils/functions/roundN";
import { Emdash } from "~/components/Punctuation";
import { AssetUserEvents } from "~/modules/analytics/events/assets";
import { Routes } from "~/routes";
import { LatestAssetRow } from "./types";

// // // //

function formatValue(value: number | null): React.ReactNode {
    // 2️⃣ Missing or very small values (avoid forcing 2 decimals)
    if (value === null || value < 1) {
        return <Emdash className="text-muted-foreground" />;
    }

    // Handle billion or greater
    if (value >= 1_000_000_000) {
        return `$${roundN(value / 1_000_000_000, 1).toLocaleString("en-US", { maximumFractionDigits: 1, minimumFractionDigits: 1 })}B`;
    }
    // Handle million or greater but less than a billion
    else if (value >= 1_000_000) {
        return `$${roundN(value / 1_000_000, 1).toLocaleString("en-US", { maximumFractionDigits: 1, minimumFractionDigits: 1 })}M`;
    }
    // Handle thousand or greater but less than a million
    else if (value >= 1_000) {
        return `$${roundN(value / 1_000, 1).toLocaleString("en-US", { maximumFractionDigits: 1, minimumFractionDigits: 1 })}K`;
    }

    // Display without any formatting if value is less than 1000
    return `$${value.toLocaleString("en-US", {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
    })}`;
}

// // // //

export function LatestAssetsCard(props: {
    assets: LatestAssetRow[];
    colSpans?: ColumnSpanConfig;
}) {
    const {
        assets,
        colSpans = {
            sm: 12,
            md: 12,
            lg: 12,
            xl: 6,
            xxl: 6,
        },
    } = props;

    // NOTE - `assets` is already limited to the New Asset Monitor's filter +
    // the registration window (see getLatestAssetRows.ts). We only sort here so
    // the card always reads newest-registration-first.
    const sortedAssets = [...assets].sort(
        (a, b) => b.created_at - a.created_at,
    );

    return (
        <TableCard
            title="Recently Registered Assets"
            tooltip="Assets registered on RWA.xyz in the last 30 days, using the same criteria as the New Asset Monitor. Registration Date reflects when an asset was added to the platform."
            colSpans={colSpans}
            watermark
            linkTo={{
                label: "View All",
                href: Routes.newAssetMonitor,
                analyticsLabel: AssetUserEvents.ClickLatestAssetsViewAll,
            }}
        >
            {/* NOTE - scroll-fade hints at the rows below the fold - see shadcn/tailwind.css */}
            <div className="scroll-fade scroll-fade-24 max-h-98 overflow-y-auto">
                <ul className="divide-y divide-border/30">
                    {sortedAssets.map((asset) => (
                        <li key={asset.id}>
                            <Link
                                href={asset.url}
                                analyticsLabel={
                                    AssetUserEvents.ClickLatestAssetsLink
                                }
                                className="group grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 pr-3 pl-5 py-1.5 text-sm transition-colors hover:bg-background-hover focus:outline-hidden focus:ring-0"
                            >
                                {/* Asset: icon + name + ticker */}
                                <div className="flex items-center gap-4 min-w-0">
                                    <ImageLoader
                                        src={asset.icon_url}
                                        alt={asset.name}
                                        className="rounded-full shrink-0 h-5 w-5 min-w-5"
                                        fallbackSrc={RWA_COIN_ICON}
                                    />
                                    <div className="flex flex-col min-w-0">
                                        <span className="truncate text-link text-sm group-hover:text-link-hover transition-colors">
                                            {asset.name}
                                        </span>
                                        {asset.ticker && (
                                            <span className="text-secondary-foreground text-xs">
                                                {asset.ticker}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="flex items-center justify-self-end gap-3">
                                    {asset.asset_class_name && (
                                        <Badge
                                            variant={BadgeVariants.gray}
                                            size="sm"
                                            rounded="md"
                                            className="max-w-30 shrink-0 gap-1.5 transition-colors group-hover:border-slate-300 group-hover:bg-slate-200 sm:max-w-[10rem]"
                                            label={
                                                <span
                                                    className="truncate"
                                                    title={
                                                        asset.asset_class_name
                                                    }
                                                >
                                                    {asset.asset_class_name}
                                                </span>
                                            }
                                        />
                                    )}

                                    {/* Value + time stacked */}
                                    <div className="flex flex-col items-end shrink-0">
                                        <span className="text-sm tabular-nums">
                                            {formatValue(asset.value)}
                                        </span>
                                        <span className="text-secondary-foreground text-xs opacity-70 group-hover:opacity-100 transition-all">
                                            <DateDisplay
                                                value={new Date(
                                                    asset.created_at,
                                                ).toISOString()}
                                            />
                                        </span>
                                    </div>
                                </div>
                            </Link>
                        </li>
                    ))}
                </ul>
            </div>
        </TableCard>
    );
}
